import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { formatearPrecioPorCodigoMoneda } from "../../../utils/moneda";

function StripeForm({ clientSecret, total, moneda, pedidoId }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [cardName, setCardName] = useState("");
  const [status, setStatus] = useState("ready");
  const [error, setError] = useState(null);

  const mapStripeError = useMemo(() => {
    return (err) => {
      const decline = err?.decline_code;
      const code = err?.code;
      const key = decline || code;

      const M = {
        insufficient_funds:
          "Tu tarjeta no tiene fondos suficientes. Intenta con otra tarjeta.",
        lost_card: "Esta tarjeta fue reportada como perdida.",
        stolen_card: "Esta tarjeta fue reportada como robada.",
        do_not_honor:
          "Tu banco rechazó el pago. Intenta con otra tarjeta o consulta con tu banco.",
        generic_decline: "Tu tarjeta fue rechazada.",
        fraudulent: "El pago fue rechazado.",
        card_declined: "Tu tarjeta fue rechazada.",
        expired_card: "Tu tarjeta está vencida.",
        incorrect_cvc: "El código de seguridad (CVC) es incorrecto.",
        incorrect_number: "El número de tarjeta es incorrecto.",
        invalid_number: "El número de tarjeta no es válido.",
        invalid_expiry_month: "El mes de vencimiento no es válido.",
        invalid_expiry_year: "El año de vencimiento no es válido.",
        invalid_cvc: "El código de seguridad (CVC) no es válido.",
        processing_error:
          "Ocurrió un error al procesar la tarjeta. Intenta nuevamente.",
        amount_too_large: "El monto es demasiado alto para esta tarjeta.",
        amount_too_small: "El monto es demasiado bajo.",
      };

      return M[key] || "No pudimos procesar el pago. Intenta nuevamente.";
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setStatus("processing");
    setError(null);

    const cardEl = elements.getElement(CardElement);

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardEl,
        billing_details: { name: cardName },
      },
    });

    if (result.error) {
      setError(mapStripeError(result.error));
      setStatus("ready");
      return;
    }

    try {
      localStorage.removeItem("cart");
      localStorage.removeItem("cart_expiration");
      localStorage.removeItem("art_expiration");
      sessionStorage.removeItem("checkout_total");

      if (pedidoId != null) {
        sessionStorage.setItem("pedido_id", String(pedidoId));
      }
    } catch {}

    navigate("/store/checkout/success", { replace: true });
  };

  return (
    <div className="nb-payment-shell">
      <form
        onSubmit={handleSubmit}
        className="nb-payment-panel nb-payment-panel--stripe"
      >
        <div className="nb-payment-brand">Stripe</div>

        <h2>Pago seguro</h2>
        <p className="nb-payment-sub">Tu pago está protegido por Stripe</p>

        <div className="nb-payment-total">
          <span>Total a pagar</span>
          <strong>
            {formatearPrecioPorCodigoMoneda(total, moneda || "USD", {
              locale: "en-US",
            }) || "—"}
          </strong>
        </div>

        <div className="nb-field">
          <label htmlFor="nb-card-name">Nombre en la tarjeta</label>
          <input
            id="nb-card-name"
            type="text"
            className="nb-card-name"
            placeholder="Nombre en la tarjeta"
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
            required
          />
        </div>

        <p className="nb-card-hint">
          Ingresa el nombre exactamente como aparece en la tarjeta
        </p>

        <div className="nb-field">
          <label>Datos de la tarjeta</label>
          <div className="nb-card-element">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#1f2937",
                    fontFamily: "Inter, system-ui, sans-serif",
                    "::placeholder": { color: "#94a3b8" },
                  },
                  invalid: { color: "#b42318" },
                },
              }}
            />
          </div>
        </div>

        {error && <p className="nb-error">{error}</p>}

        <button
          className="nb-btn nb-btn--primary nb-pay-btn"
          disabled={status === "processing"}
        >
          {status === "processing" ? "Procesando..." : "Pagar de forma segura"}
        </button>
      </form>
    </div>
  );
}

export default function CheckoutPaymentStripe({ initialData }) {
  const navigate = useNavigate();

  const publicKey =
    (initialData?.public_key && String(initialData.public_key).trim()) ||
    process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    "";

  const clientSecret = String(initialData?.client_secret || "").trim();

  if (!publicKey) {
    return (
      <div className="nb-payment-shell">
        <div className="nb-payment-panel nb-payment-panel--error">
          <div className="nb-payment-icon nb-payment-icon--error">!</div>
          <h2>Stripe no disponible</h2>
          <p>No se encontró la llave pública de Stripe.</p>
          <div className="nb-payment-actions">
            <button
              type="button"
              className="nb-btn nb-btn--primary"
              onClick={() => navigate("/store/cart")}
            >
              Volver al carrito
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="nb-payment-shell">
        <div className="nb-payment-panel nb-payment-panel--error">
          <div className="nb-payment-icon nb-payment-icon--error">!</div>
          <h2>Pago no disponible</h2>
          <p>No se recibió el client secret de Stripe.</p>
          <div className="nb-payment-actions">
            <button
              type="button"
              className="nb-btn nb-btn--primary"
              onClick={() => navigate("/store/cart")}
            >
              Volver al carrito
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stripePromise = loadStripe(publicKey, { locale: "en" });

  return (
    <Elements stripe={stripePromise} options={{ locale: "en" }}>
      <StripeForm
        clientSecret={clientSecret}
        total={initialData?.total_factura}
        moneda={initialData?.moneda}
        pedidoId={initialData?.pedido_id}
      />
    </Elements>
  );
}